import { useQuery } from '@tanstack/react-query';

import Api from '@/services/api';
import type { BaseContext } from '@/types';
import { ROSA_QUERY_KEYS } from './query-keys';

export type GetResultRequest = BaseContext & {
	id: string;
	finished?: boolean;
};

type Information = {
	report_name: string;
	sector_name: string;
	line_name: string;
	activity_name: string;
	evaluator_name: string;
	company_name: string;
	original_name: string;
	collection_date: string;
	assessment_date: string;
	workstation_name: string;
	organization_name: string;
	organization_id: string;
	company_id: string;
	sector_id: string;
	workstation_id: string;
	activity_id: string;
	evaluator_id: string;
	file_id: string;
};

type Scores = {
	final_score: number;
	section_a: number;
	section_b: number;
	section_c: number;
	section_d: number;
};

type Image = {
	description: string;
	url: string;
};

type SubCategory = {
	sub_category: string;
	type: string;
	sequence?: number;
	description?: string;
	required: boolean;
	score: number;
};

type Section = {
	name: string;
	sub_categories: SubCategory[][];
};

type RequiredAction = {
	name: string;
	action: string;
	priority: number;
	id: string;
};

type Deadline = {
	full_date: string;
	day: string;
	month: string;
	year: string;
	remaining_days: number;
	remaining_days_text: string;
};

type Task = {
	id: string;
	description: string;
	type: string;
};

type ActionPlan = {
	responsible: string;
	deadline: Deadline;
	tasks: Task[] | null;
	title: string;
	id: string;
	description: string;
};

export type RosaReportResult = {
	id: string;
	informations: Information;
	scores: Scores;
	images: Image[];
	sections: Section[];
	required: RequiredAction[];
	action_plans: ActionPlan[];
	comment: string;
	consolidated: boolean;
};

async function getResults(params: GetResultRequest): Promise<RosaReportResult> {
	const url = `ergonomic-tool/rosa/result/${params.id}`;
	const { data } = await Api.get(url, { params });
	return data;
}

export const useGetResult = ({ id, organization_id, company_id, finished }: GetResultRequest) => {
	const parameters = { organization_id, company_id, id };
	const report = useQuery([ROSA_QUERY_KEYS.GET_RESULT, parameters], () => getResults(parameters), {
		enabled: !!organization_id && !!company_id && !!id && !!finished
	});
	return {
		...report,
		data: report.data ?? ({} as RosaReportResult)
	};
};
